import {
  readPreviousAnswer,
  isChoiceValue,
  type ChoiceValue,
  type AnswerJson,
} from "../answerJson";
import { decodeVisualGroups, type VisualGrouping } from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { SelectTypes } from "../../util/types";
import type { QuestionCore } from "../contracts";
import { restoreChoice } from "../specify/core";

export type SelectOption = {
  id: number | string;
  label: string;
  disabled: boolean;
  displayLabel: boolean;
  image?: string;
  customPrompt?: string;
};

export type SelectDefinition = VisualGrouping & {
  type: "select";
  prompt: string;
  image?: string;
  altText?: string;
  error?: string;
  options: readonly SelectOption[];
  previousResponse?: ChoiceValue;
};

export type SelectDraft = {
  selectedOptionId: number | string | null;
  customText: string;
};

export type SelectEvent =
  | { type: "select"; optionId: number | string }
  | { type: "set-custom-text"; value: string }
  | {
      type: "set-custom-answer";
      optionId: number | string;
      value: string;
    }
  | { type: "clear" };

export type SelectResponse = {
  type: "select";
  value: number | string;
  answerJson: AnswerJson;
};

const optionFor = (definition: SelectDefinition, draft: SelectDraft) =>
  definition.options.find((option) => option.id === draft.selectedOptionId);

export const selectCore: QuestionCore<
  SelectTypes,
  SelectDefinition,
  SelectDraft,
  SelectEvent,
  SelectResponse
> = {
  decode: (raw) => ({
    ...decodeVisualGroups(raw),
    type: "select",
    prompt: raw.content,
    ...(raw.image ? { image: raw.image } : {}),
    ...(raw.alt_text ? { altText: raw.alt_text } : {}),
    ...(raw.error_message ? { error: raw.error_message } : {}),
    options: raw.options.map((option) => ({
      id: option.value,
      label: option.text,
      disabled: option.disabled,
      displayLabel: option.display_text,
      ...(option.image ? { image: option.image } : {}),
      ...(option.pop_up_text != null
        ? { customPrompt: option.pop_up_text }
        : {}),
    })),
    ...(raw.previous_answer_json !== undefined ||
    raw.previous_response !== undefined
      ? {
          previousResponse: readPreviousAnswer(
            raw.previous_answer_json,
            raw.previous_response,
            isChoiceValue,
          ),
        }
      : {}),
  }),
  initialize: (definition) => {
    const restored = restoreChoice(
      definition.options,
      definition.previousResponse,
    );
    return {
      selectedOptionId: restored?.optionId ?? null,
      customText: restored?.customText ?? "",
    };
  },
  update: (definition, draft, event) => {
    if (event.type === "clear") {
      return { selectedOptionId: null, customText: "" };
    }
    if (event.type === "set-custom-answer") {
      const option = definition.options.find(
        (item) => item.id === event.optionId,
      );
      return option &&
        !option.disabled &&
        option.customPrompt !== undefined &&
        event.value.trim()
        ? { selectedOptionId: option.id, customText: event.value }
        : draft;
    }
    if (event.type === "set-custom-text") {
      return { ...draft, customText: event.value };
    }
    const option = definition.options.find(
      (candidate) => candidate.id === event.optionId,
    );
    if (!option || option.disabled) return draft;
    if (option.id === draft.selectedOptionId) return draft;
    return { selectedOptionId: option.id, customText: "" };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) => {
    const option = optionFor(definition, draft);
    if (!option || option.disabled) {
      return { valid: false, error: copy.validation.selectAvailable };
    }
    if (option.customPrompt !== undefined && !draft.customText.trim()) {
      return {
        valid: false,
        error: copy.validation.selectCustomAnswer,
        fieldId: `select-${option.id}-custom`,
      };
    }
    return { valid: true };
  },
  encode: (definition, draft) => {
    const validation = selectCore.validate(definition, draft);
    const option = optionFor(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      type: "select",
      answerJson:
        option!.customPrompt !== undefined
          ? { custom: draft.customText }
          : typeof option!.id === "number"
            ? { option: option!.id }
            : option!.id,
      value:
        option!.customPrompt !== undefined
          ? draft.customText.trim()
          : option!.id,
    };
  },
  projectTranscript: (definition, draft) => {
    const option = optionFor(definition, draft);
    if (!option) {
      throw new Error("Cannot project an unanswered select question.");
    }
    return {
      kind: "select",
      question: definition.prompt,
      answer:
        option.customPrompt !== undefined
          ? draft.customText.trim()
          : option.label,
      optionId: option.id,
    };
  },
};
